import {
  DavinciSsoDeferState,
  DavinciSsoFailureCode,
  DavinciSsoScenario,
  SilentSsoFailureReason,
  SsoPhase,
} from './auth-sso-attempt.model';

export const SSO_DEFER_STORAGE_KEY = 'ph-auth-sso-defer';
/**
 * Okta IdP cookie was already alive before this portal acquired tokens (sibling MFA).
 * localStorage, not sessionStorage: it describes the Okta tokens, which every tab of this origin shares.
 */
export const IDP_PREEXISTED_STORAGE_KEY = 'ph-auth-idp-preexisted';
export const CALLBACK_DEFERRED_TO_LOGIN_KEY = 'ph-auth-callback-deferred-to-login';
export const SSO_PHASE_STORAGE_KEY = 'ph-auth-sso-phase';
/** Set after "log out of all sessions" — skip silent SSO until interactive login widget loads. */
export const POST_LOGOUT_ALL_KEY = 'ph-auth-post-logout-all';
/** Bound Okta IdP session id for this tab (custom AS id tokens often omit `sid`). */
export const BOUND_OKTA_SESSION_KEY = 'ph-auth-okta-bound-session';

export interface BoundOktaSession {
  application: string;
  id: string;
  userId?: string;
  recordedAt: string;
}

interface SsoPhaseState {
  application: string;
  phase: SsoPhase;
  failureReason?: SilentSsoFailureReason;
  recordedAt: string;
}

function readApplicationScopedFlag(key: string, application: string, storage: Storage = sessionStorage): boolean {
  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return false;
    }
    const parsed = JSON.parse(raw) as { application: string };
    return parsed.application === application;
  } catch {
    return false;
  }
}

function writeApplicationScopedFlag(key: string, application: string, storage: Storage = sessionStorage): void {
  try {
    storage.setItem(key, JSON.stringify({ application, recordedAt: new Date().toISOString() }));
  } catch {
    /* ignore */
  }
}

function clearApplicationScopedFlag(key: string, storage: Storage = sessionStorage): void {
  try {
    storage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function markCallbackDeferredToLogin(application: string): void {
  writeApplicationScopedFlag(CALLBACK_DEFERRED_TO_LOGIN_KEY, application);
}

export function readCallbackDeferredToLogin(application: string): boolean {
  return readApplicationScopedFlag(CALLBACK_DEFERRED_TO_LOGIN_KEY, application);
}

export function clearCallbackDeferredToLogin(): void {
  clearApplicationScopedFlag(CALLBACK_DEFERRED_TO_LOGIN_KEY);
}

function readSsoPhaseState(application: string): SsoPhaseState | null {
  try {
    const raw = sessionStorage.getItem(SSO_PHASE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as SsoPhaseState;
    return parsed.application === application ? parsed : null;
  } catch {
    return null;
  }
}

export function readSsoPhase(application: string): SsoPhase {
  return readSsoPhaseState(application)?.phase ?? 'none';
}

export function isSilentSsoTerminal(application: string): boolean {
  return readSsoPhase(application) === 'silent-failed';
}

export function isSilentSsoInFlight(application: string): boolean {
  return readSsoPhase(application) === 'silent-in-flight';
}

/**
 * `prompt=none` answers `login_required` when there is no Okta session; `access_denied` means a live
 * session belongs to an account not assigned to this portal's OIDC client (e.g. Lithia user opening GM).
 */
export function isForeignSessionSilentSsoDenial(
  application: string,
  oauthErrorCode: string,
  enforceSinglePortalSession = true
): boolean {
  return enforceSinglePortalSession && oauthErrorCode === 'access_denied' && isSilentSsoInFlight(application);
}

function writeSsoPhase(
  application: string,
  phase: SsoPhase,
  failureReason?: SilentSsoFailureReason
): void {
  try {
    const state: SsoPhaseState = {
      application,
      phase,
      recordedAt: new Date().toISOString(),
      ...(failureReason ? { failureReason } : {}),
    };
    sessionStorage.setItem(SSO_PHASE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export function markSilentSsoInFlight(application: string): void {
  writeSsoPhase(application, 'silent-in-flight');
}

export function markSilentSsoFailed(application: string, failureReason: SilentSsoFailureReason): void {
  writeSsoPhase(application, 'silent-failed', failureReason);
}

export function markOktaTokensAcquired(application: string): void {
  writeSsoPhase(application, 'tokens-acquired');
}

export function clearSsoPhase(): void {
  clearApplicationScopedFlag(SSO_PHASE_STORAGE_KEY);
}

export function markPostLogoutAll(application: string): void {
  writeApplicationScopedFlag(POST_LOGOUT_ALL_KEY, application);
}

export function readPostLogoutAll(application: string): boolean {
  return readApplicationScopedFlag(POST_LOGOUT_ALL_KEY, application);
}

export function clearPostLogoutAll(): void {
  clearApplicationScopedFlag(POST_LOGOUT_ALL_KEY);
}

export function readBoundOktaSession(application: string): BoundOktaSession | null {
  try {
    const raw = sessionStorage.getItem(BOUND_OKTA_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as BoundOktaSession;
    if (parsed.application !== application || !parsed.id) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeBoundOktaSession(
  application: string,
  session: { id: string; userId?: string | null }
): void {
  try {
    const payload: BoundOktaSession = {
      application,
      id: session.id,
      recordedAt: new Date().toISOString(),
    };
    if (session.userId) {
      payload.userId = session.userId;
    }
    sessionStorage.setItem(BOUND_OKTA_SESSION_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function clearBoundOktaSession(): void {
  clearApplicationScopedFlag(BOUND_OKTA_SESSION_KEY);
}

export interface LiveIdpSession {
  id: string;
  userId?: string;
}

/**
 * Logged-in tab vs live Okta session. Do not persist here — capture at login/home only.
 * ponytail: no stored bind + matching userIds cannot detect same-user session replacement.
 */
export function isBoundIdpSessionStillCurrent(
  live: LiveIdpSession | null,
  bound: BoundOktaSession | null,
  idTokenSub: string | null
): boolean {
  // ponytail: 404 with no bind captured = Okta cookie never visible to this tab (3P cookies blocked,
  // incognito), not "session ended". Ceiling: a tab whose capture never succeeds never auto-closes.
  if (!live) {
    return !bound;
  }
  if (idTokenSub && live.userId && live.userId !== idTokenSub) {
    return false;
  }
  if (bound?.id && bound.id !== live.id) {
    return false;
  }
  return true;
}

/** Pull a human-readable message from an API error body (flat or `{ error: { message } }`). */
function messageFromErrorBody(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (!value || typeof value !== 'object') {
    return '';
  }

  const record = value as { message?: unknown; responseDescription?: unknown; error?: unknown };
  if (typeof record.message === 'string' && record.message) {
    return record.message;
  }
  if (typeof record.responseDescription === 'string' && record.responseDescription) {
    return record.responseDescription;
  }
  if ('error' in record) {
    return messageFromErrorBody(record.error);
  }

  return '';
}

export function extractHttpErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'error' in error) {
    const fromBody = messageFromErrorBody((error as { error: unknown }).error);
    if (fromBody) {
      return fromBody;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  // ponytail: avoid String(HttpErrorResponse) → "[object Object]"
  return '';
}

function httpStatusFromError(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }
  return undefined;
}

export function classifySsoFailure(error: unknown): DavinciSsoFailureCode {
  const fromMessage = classifySsoFailureMessage(extractHttpErrorMessage(error));
  if (fromMessage !== 'UNKNOWN') {
    return fromMessage;
  }
  // ponytail: any non-2xx HTTP status is a backend error (DaVinci 330, 500, etc.).
  // Ceiling: a future status that means something other than "server problem" would need its own branch.
  const status = httpStatusFromError(error);
  return status && (status < 200 || status >= 300) ? 'SERVER_ERROR' : 'UNKNOWN';
}

export function classifySsoFailureMessage(message: string): DavinciSsoFailureCode {
  const normalized = message.toLowerCase();
  if (normalized.includes('no roles assigned')) {
    return 'NO_ROLES';
  }
  if (normalized.includes('no dealer associated with this user')) {
    return 'NO_DEALER_ASSOCIATED_WITH_USER';
  }
  if (normalized.includes('portal user not found')) {
    return 'PORTAL_USER_NOT_FOUND';
  }
  if (normalized.includes('not associated with this portal') || normalized.includes('wrong portal')) {
    return 'WRONG_PORTAL';
  }
  if (normalized.includes('500 - ')) {
    return 'SERVER_ERROR';
  }

  return 'UNKNOWN';
}

export function resolveSsoScenario(code: DavinciSsoFailureCode): DavinciSsoScenario {
  if (code === 'NO_ROLES') {
    return 'NO_ROLES';
  }
  if (code === 'NO_DEALER_ASSOCIATED_WITH_USER') {
    return 'NO_DEALER_ASSOCIATED_WITH_USER';
  }
  if (code === 'PORTAL_USER_NOT_FOUND' || code === 'WRONG_PORTAL') {
    return 'PORTAL_SETUP';
  }
  if (code === 'SERVER_ERROR') {
    return 'SERVER_ERROR';
  }

  return 'GENERIC';
}

export function markIdpSessionPreexisted(application: string): void {
  writeApplicationScopedFlag(IDP_PREEXISTED_STORAGE_KEY, application, localStorage);
}

/**
 * Tokens returned by our own `prompt=none` request prove an Okta session existed before this tab
 * signed in. Covers incognito, where `/sessions/me` can't see the cookie. Call before the phase moves on.
 */
export function markIdpSessionPreexistedIfSilentSso(application: string): boolean {
  if (!isSilentSsoInFlight(application)) {
    return false;
  }
  markIdpSessionPreexisted(application);
  return true;
}

export function readIdpSessionPreexisted(application: string): boolean {
  return readApplicationScopedFlag(IDP_PREEXISTED_STORAGE_KEY, application, localStorage);
}

export function clearIdpSessionPreexisted(): void {
  clearApplicationScopedFlag(IDP_PREEXISTED_STORAGE_KEY, localStorage);
}

export function clearSsoTabState(): void {
  clearDavinciSsoDefer();
  clearIdpSessionPreexisted();
  clearCallbackDeferredToLogin();
  clearSsoPhase();
  clearBoundOktaSession();
}

function resolveDeferScenario(
  error: unknown,
  code: DavinciSsoFailureCode,
  treatPreexistingIdpAsConflict: boolean
): DavinciSsoScenario {
  if (error == null) {
    return 'CROSS_PORTAL_CONFLICT';
  }

  // A 5xx is a backend failure, not a sibling session; 401/unclassified still defer to conflict.
  if ((httpStatusFromError(error) ?? 0) >= 500) {
    return 'SERVER_ERROR';
  }

  if (treatPreexistingIdpAsConflict) {
    return 'CROSS_PORTAL_CONFLICT';
  }

  const resolved = resolveSsoScenario(code);
  if (resolved === 'GENERIC') {
    return 'NO_ROLES';
  }

  return resolved;
}

/**
 * Persist a tab-local SSO failure after getOktaUser / ssologin errors.
 *
 * ponytail: preexisting IdP cookie ⇒ conflict (Lithia→Audi), except backend 5xx ⇒ SERVER_ERROR.
 * Ceiling: Okta dashboard (no Phoenix) + 401 looks like sibling MFA. error == null is IdP eject.
 * enforceSinglePortalSession false: classify DaVinci instead of forcing conflict.
 */
export function recordDavinciSsoDefer(
  application: string,
  error: unknown,
  enforceSinglePortalSession = true
): DavinciSsoDeferState {
  const code = classifySsoFailure(error);
  const treatPreexistingIdpAsConflict =
    enforceSinglePortalSession && readIdpSessionPreexisted(application);

  const defer: DavinciSsoDeferState = {
    application,
    code,
    message: extractHttpErrorMessage(error),
    scenario: resolveDeferScenario(error, code, treatPreexistingIdpAsConflict),
    recordedAt: new Date().toISOString(),
  };

  try {
    sessionStorage.setItem(SSO_DEFER_STORAGE_KEY, JSON.stringify(defer));
  } catch {
    // ponytail: sessionStorage can fail in private mode; defer is best-effort
  }

  return defer;
}

export function readDavinciSsoDefer(application: string): DavinciSsoDeferState | null {
  try {
    const raw = sessionStorage.getItem(SSO_DEFER_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as DavinciSsoDeferState;
    return parsed.application === application ? parsed : null;
  } catch {
    return null;
  }
}

export function clearDavinciSsoDefer(): void {
  try {
    sessionStorage.removeItem(SSO_DEFER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

